import { useParams, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/pages/home/components/Footer';
import { articleData, allArticles } from '@/mocks/article';
import { categoryArticles } from '@/mocks/category';

function generateArticleSchema(article: NonNullable<ReturnType<() => typeof articleData[keyof typeof articleData]>>) {
  return {
    '@context': 'https://schema.org',
    '@type': 'NewsArticle',
    headline: article.title,
    description: article.content[0],
    image: article.image,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.time,
    publisher: {
      '@type': 'Organization',
      name: '爆料专区',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://aalanfm.cn/article/${article.id}`,
    },
  };
}

function getArticle(id: string) {
  const detailedArticle = articleData[id];

  if (detailedArticle) {
    return detailedArticle;
  }

  const summaryArticle = categoryArticles.find((item) => String(item.id) === id);

  if (!summaryArticle) {
    return null;
  }

  return {
    id: summaryArticle.id,
    title: summaryArticle.title,
    category: summaryArticle.category,
    tag: summaryArticle.tag,
    time: summaryArticle.time,
    reads: summaryArticle.reads,
    author: '爆料专区编辑部',
    image: summaryArticle.image,
    content: [
      summaryArticle.summary,
      `围绕「${summaryArticle.title}」的相关线索仍在持续发酵，爆料专区会结合公开信息和多方反馈继续跟进。`,
      `更多来自${summaryArticle.category}的后续动态，将在完成基础核实后第一时间更新，方便读者集中查看事件进展。`,
    ],
    relatedArticles: allArticles
      .filter((item) => item.id !== summaryArticle.id)
      .slice(0, 3)
      .map((item) => item.id),
  };
}

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const article = id ? getArticle(id) : null;

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-lg text-foreground-500 mb-4">文章不存在或已被删除</p>
          <a href="/" className="text-primary-500 font-semibold hover:text-primary-600 transition-colors whitespace-nowrap">
            返回首页
          </a>
        </div>
      </div>
    );
  }

  const relatedArticles = article.relatedArticles
    .map((rid) => allArticles.find((a) => a.id === rid))
    .filter(Boolean);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main>
        <article className="pt-24 md:pt-28 pb-12 md:pb-16 px-4 md:px-8">
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{ __html: JSON.stringify(generateArticleSchema(article)) }}
          />
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center gap-2 mb-6">
              <button
                onClick={() => navigate('/')}
                className="text-sm text-foreground-500 hover:text-foreground-800 transition-colors whitespace-nowrap cursor-pointer"
              >
                首页
              </button>
              <i className="ri-arrow-right-s-line text-foreground-400 text-sm"></i>
              <button
                onClick={() => navigate(-1)}
                className="text-sm text-foreground-500 hover:text-foreground-800 transition-colors whitespace-nowrap cursor-pointer"
              >
                返回
              </button>
            </div>

            <div className="flex items-center gap-3 mb-5">
              <span className={`px-2.5 py-1 rounded-full text-[11px] font-bold whitespace-nowrap ${
                article.tag === '爆'
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-accent-500 text-background-50'
              }`}>
                {article.tag}
              </span>
              <span className="text-xs text-foreground-500 bg-background-100 px-2.5 py-1 rounded-full whitespace-nowrap">
                {article.category}
              </span>
            </div>

            <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-black text-foreground-950 leading-tight mb-4">
              <strong>{article.title}</strong>
            </h1>

            <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500 mb-8 pb-8 border-b border-background-200">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-line"></i>
                {article.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line"></i>
                {article.time}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-eye-line"></i>
                {article.reads} 阅读
              </span>
            </div>

            <div className="mb-10 rounded-xl overflow-hidden">
              <img
                src={article.image}
                alt={article.title}
                title={`${article.title} - 爆料专区独家报道`}
                className="w-full h-auto object-cover"
              />
            </div>

            <div className="prose-custom">
              {article.content.map((paragraph, index) => (
                <p
                  key={index}
                  className="text-base md:text-lg text-foreground-700 leading-relaxed mb-5"
                >
                  {paragraph}
                </p>
              ))}
            </div>

            <div className="mt-12 pt-8 border-t border-background-200">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-heading font-bold text-foreground-950">
                  相关爆料
                </h3>
                <a href="/" className="text-sm text-primary-500 hover:text-primary-600 transition-colors whitespace-nowrap">
                  查看更多 →
                </a>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {relatedArticles.map((ra) => ra && (
                  <a
                    key={ra.id}
                    href={`/article/${ra.id}`}
                    className="group block p-4 rounded-xl bg-background-100 hover:-translate-y-1 transition-all duration-300"
                  >
                    <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold mb-2 whitespace-nowrap ${
                      ra.tag === '爆'
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-accent-500 text-background-50'
                    }`}>
                      {ra.tag}
                    </span>
                    <h4 className="text-sm font-bold text-foreground-900 group-hover:text-primary-500 transition-colors line-clamp-2 mb-2">
                      {ra.title}
                    </h4>
                    <div className="flex items-center justify-between text-xs text-foreground-500">
                      <span>{ra.time}</span>
                      <span className="flex items-center gap-1">
                        <i className="ri-eye-line text-[10px]"></i>
                        {ra.reads}
                      </span>
                    </div>
                  </a>
                ))}
              </div>
            </div>
          </div>
        </article>
      </main>

      <Footer />
    </div>
  );
}
